<template>
  <div class="d-flex flex-column align-items-end gap-2">
    <DropDownInput
      id="user-spend-chart-toggle"
      :value="selectedChartType"
      class="me-2"
      :options="chartTypeOpts"
      label=""
      @selected-option="setChartType"
    />
    <BaseLineChart
      v-if="selectedChartType === 'line'"
      class="w-100 h-100"
      :primary-data="chartData?.current"
      :secondary-data="chartData?.relative"
      :data="sortedData"
    />
    <BaseGroupedBarChart
      v-else
      class="w-100 h-100"
      :primary-data="chartData?.current"
      :secondary-data="chartData?.relative"
      :data="sortedData"
    />
  </div>
</template>
<script>
import { computed, ref } from "vue";
import BaseLineChart from "./charts/BaseLineChart.vue";
import BaseGroupedBarChart from "./charts/BaseGroupedBarChart.vue";
import { useStore } from "vuex";
import { useDiffCalculator } from "@/composables/Helpers";
import DropDownInput from "./helpers/DropDownInput.vue";

export default {
  components: { BaseLineChart, BaseGroupedBarChart, DropDownInput },
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
    chartSumData: {
      type: Object,
      default: () => {},
    },
  },
  emits: ["update:chartSumData"],
  setup(props, { emit }) {
    const store = useStore();
    const chartTypeOpts = ref([
      { key: "line", value: "Line Chart" },
      { key: "bar", value: "Bar Chart" },
    ]);
    const selectedChartType = ref("line");
    const timeFilters = computed(() => store.state.overViewStore.timeFilters);
    const metricFilters = computed(
      () => store.state.overViewStore.metricFilters
    );

    const chartSumDataComputed = computed(() => {
      let data = {
        avgSum: 0,
        relAvgSum: 0,
        totalSum: 0,
        relTotalSum: 0,
        avgDiffPercentage: null,
        totalDiffPercentage: null,
      };

      // == get Total spend object keys based on different days count ==
      const timeDaysCount = timeFilters.value?.periodFilter ?? 30;
      const { current: curTotalKey, relative: relTotalKey } =
        getTotalKeys(timeDaysCount);

      const pointsCount = (props.data ?? []).length ?? 0;

      //if empty data, => exit
      if (pointsCount === 0) {
        emit("update:chartSumData", data);
        return data;
      }

      for (let dataPoint in props.data) {
        const dataObj = props.data[dataPoint];
        // console.log(dataObj);

        // === [1] average sum for current and relative values ===
        data.avgSum += Math.abs(dataObj.tenant_customers_avg_spend);
        data.relAvgSum += Math.abs(
          dataObj.comparison_period_tenant_customers_avg_spend
        );

        // === [2] total sum for current and relative values ===
        data.totalSum += Math.abs(dataObj[curTotalKey]);
        data.relTotalSum += Math.abs(dataObj[relTotalKey]);
      }

      // console.log("Points: ", pointsCount);
      // console.log("data.avgSum: ", data.avgSum);
      // console.log("data.relAvgSum: ", data.relAvgSum);
      // console.log("data.totalSum: ", data.totalSum);
      // console.log("data.relTotalSum: ", data.relTotalSum);

      // === round all values ===
      data = {
        avgSum: Math.round(data.avgSum / pointsCount),
        relAvgSum: Math.round(data.relAvgSum / pointsCount),
        totalSum: Math.round(data.totalSum),
        relTotalSum: Math.round(data.relTotalSum),
      };

      // == [3] difference percenrtage ==
      data.avgDiffPercentage = useDiffCalculator(data.avgSum, data.relAvgSum);
      data.totalDiffPercentage = useDiffCalculator(
        data.totalSum,
        data.relTotalSum
      );
      // == [4] emit v-model value for parent component ==
      emit("update:chartSumData", data);
      return data;
    });
    chartSumDataComputed.value;

    const sortedData = computed(() => {
      let dataList = Object.values(props.data ?? {});
      return dataList.sort((a, b) => {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        return (
          new Date(a?.tenant_transactions_date) -
          new Date(b?.tenant_transactions_date)
        );
      });
    });

    const chartData = computed(() => {
      let data = { current: [], relative: [] };

      const timeDaysCount = timeFilters.value?.periodFilter ?? 30;
      const { current: curTotalKey, relative: relTotalKey } =
        getTotalKeys(timeDaysCount);

      //Check if empty data, => exit
      const pointsCount = (props.data ?? [])?.length ?? 0;
      if (pointsCount === 0) return data;

      //Format the data
      for (let dataPoint in props.data) {
        const dataObj = props.data[dataPoint];

        // == [1] set current data points ==
        data.current.push({
          x: dataObj?.tenant_transactions_date,
          y:
            metricFilters.value.key === "average"
              ? Math.round(Math.abs(dataObj?.tenant_customers_avg_spend))
              : Math.round(Math.abs(dataObj[curTotalKey])),
        });

        // == [2] set relative data points ==
        data.relative.push({
          x: dataObj?.tenant_transactions_date,
          y:
            metricFilters.value.key === "average"
              ? Math.round(
                  Math.abs(
                    dataObj?.comparison_period_tenant_customers_avg_spend
                  )
                )
              : Math.round(Math.abs(dataObj[relTotalKey])),
        });
      }

      return {
        current: data.current.sort((a, b) => {
          return new Date(a.x) - new Date(b.x);
        }),
        relative: data.relative.sort((a, b) => {
          return new Date(a.x) - new Date(b.x);
        }),
      };
    });

    function setChartType(selectedValue) {
      selectedChartType.value = selectedValue;
    }

    function getTotalKeys(daysCount) {
      let totalObjKey = {
        current: "tenant_customers_total_spend",
        relative: "comparison_period_tenant_customers_total_spend",
      };

      return totalObjKey;
    }

    return {
      chartData,
      chartSumDataComputed,
      chartTypeOpts,
      selectedChartType,
      setChartType,
      sortedData,
    };
  },
};
</script>
<style></style>
